import { useState, useCallback } from 'react';
import { buildCyclePerformanceReport } from '../core/dateHelpers.js';

export function useCycleManager({
    cycleOpenedAt, currentCycle,
    sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
    setCycleHistory, setCycleOpenedAt,
    setSgBudgetCycle1, setSgBudgetCycle2, setLkBudgetDamith, setLkBudgetShan,
    authUser, isOnline, acquireDriveToken,
    DriveAPI,
}) {
    const [showCycleConfirm, setShowCycleConfirm] = useState(false);
    const [cycleResetStatus, setCycleResetStatus] = useState('idle'); // idle | exporting | done | error

    // ── handleCloseCycle: export → archive → reset ────────────────
    const handleCloseCycle = useCallback(async () => {
        if (cycleResetStatus === 'exporting') return;
        setCycleResetStatus('exporting');

        const { cycleId, cycleStart, cycleEnd, label } = cycleOpenedAt
            ? (() => {
                // Build the period info for the cycle being closed (cycleOpenedAt)
                const [y, m] = cycleOpenedAt.split('-').map(Number);
                const pad = n => String(n).padStart(2, '0');
                const cycleStart = `${cycleOpenedAt}-16`;
                let endYear = y, endMonth = m; // endMonth is 1-indexed
                endMonth += 1;
                if (endMonth > 12) { endMonth = 1; endYear += 1; }
                const cycleEnd = `${endYear}-${pad(endMonth)}-15`;
                const startDate = new Date(y, m - 1, 16);
                const endDate = new Date(endYear, endMonth - 1, 15);
                const fmtOpts = { day: 'numeric', month: 'short' };
                const lbl = `${startDate.toLocaleDateString(undefined, fmtOpts)} – ${endDate.toLocaleDateString(undefined, { ...fmtOpts, year: 'numeric' })}`;
                return { cycleId: cycleOpenedAt, cycleStart, cycleEnd, label: lbl };
            })()
            : currentCycle;

        // Build the performance report
        const report = buildCyclePerformanceReport(cycleId, cycleStart, cycleEnd, label, {
            sgCycle1: sgBudgetCycle1,
            sgCycle2: sgBudgetCycle2,
            lkA: lkBudgetDamith,
            lkB: lkBudgetShan,
        });

        // Attempt Drive export (non-blocking — reset proceeds even if upload fails)
        let driveExported = false;
        let driveExportError = null;
        if (authUser && isOnline) {
            try {
                const token = await acquireDriveToken();
                await DriveAPI.writeBudgetPerformanceFile(token, report);
                driveExported = true;
                console.log(`[iPlanner] ✅ Budget cycle exported: iplanner-budget-${cycleId}.json`);
            } catch (err) {
                driveExportError = err.message;
                console.warn('[iPlanner] Budget export failed (reset will still proceed):', err);
            }
        }

        // Archive to cycleHistory — enrich with drive export status
        const snapshot = {
            cycleId,
            cycleStart,
            cycleEnd,
            label,
            closedAt: new Date().toISOString(),
            driveExported,
            driveExportError: driveExportError || null,
            // Full item snapshots
            sgCycle1: sgBudgetCycle1.map(i => ({ ...i })),
            sgCycle2: sgBudgetCycle2.map(i => ({ ...i })),
            lkA: lkBudgetDamith.map(i => ({ ...i })),
            lkB: lkBudgetShan.map(i => ({ ...i })),
        };
        setCycleHistory(prev => [snapshot, ...prev.filter(c => c.cycleId !== snapshot.cycleId)]);

        // Reset actuals & spendLog on all live items
        const reset = items => items.map(i => ({ ...i, actualSpent: 0, spendLog: [] }));
        setSgBudgetCycle1(reset(sgBudgetCycle1));
        setSgBudgetCycle2(reset(sgBudgetCycle2));
        setLkBudgetDamith(reset(lkBudgetDamith));
        setLkBudgetShan(reset(lkBudgetShan));

        // Advance to the new cycle
        setCycleOpenedAt(currentCycle.cycleId);
        setCycleResetStatus(driveExported ? 'done' : (driveExportError ? 'error' : 'done'));
        setTimeout(() => {
            setCycleResetStatus('idle');
            setShowCycleConfirm(false);
        }, 2200);
    }, [
        cycleResetStatus, cycleOpenedAt, currentCycle,
        sgBudgetCycle1, sgBudgetCycle2, lkBudgetDamith, lkBudgetShan,
        authUser, isOnline, acquireDriveToken,
        DriveAPI,
        setCycleHistory, setCycleOpenedAt,
        setSgBudgetCycle1, setSgBudgetCycle2, setLkBudgetDamith, setLkBudgetShan,
    ]);

    return { showCycleConfirm, setShowCycleConfirm, cycleResetStatus, handleCloseCycle };
}
